import { SidebarCollapseIcon } from '@components/Icons/SidebarCollapseIcon';
import { SwipeableDrawer } from '@mui/material';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import css from './MobileNav.module.scss';
import { isMobileNavCornerClaimed } from './mobileNavCorner';
import { DesktopNav } from './Nav';

/**
 * Mobile uses the exact desktop sidebar in an overlay drawer, opened by a floating button in the
 * top-left corner. A page that puts its own control there (a back arrow) hides the button; see
 * `useClaimMobileNavCorner`.
 *
 * One nav implementation for both breakpoints keeps destinations, chat actions and account
 * controls from drifting apart.
 *
 * (The mobile --nav-drawer-width: 0 lives in root.scss as a CSS floor — no JS
 * needed here; the desktop rail clears its inline override on unmount.)
 */
export const MobileNav = observer(function MobileNav() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const { pathname } = useLocation();

  // Catch-all close-on-navigate for programmatic route changes (logout, etc.).
  // Same-route / query-only taps don't change pathname, so the nav handlers
  // (Nav.tsx) also close explicitly on tap — this is the belt to their braces.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      {/* Same icon component as the desktop sidebar's collapse/expand toggle —
          swap it there and this stays in sync. */}
      {!isMobileNavCornerClaimed() && (
        <button
          className={css.menuButton}
          aria-label="Open navigation"
          onClick={() => setOpen(true)}
        >
          <SidebarCollapseIcon />
        </button>
      )}
      <SwipeableDrawer
        anchor="left"
        open={open}
        onClose={close}
        onOpen={() => setOpen(true)}
        // Keep the sidebar mounted so its once-per-session work (chat list fetch
        // in RecentChatsSection) runs once, not on every open — matching the
        // permanently-mounted desktop rail.
        keepMounted
        className={css.drawer}
      >
        <DesktopNav floating onFloatingClose={close} />
      </SwipeableDrawer>
    </>
  );
});
